import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Slider,
  Switch,
  FormControlLabel,
  CircularProgress,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { Link } from "react-router-dom";
import _request from "actions/_request";
import AppContext from "AppContext";
import BuyCreditsModal from "components/BuyCreditsModal";
import {
  creditCostFor,
  extractCreditShortfall,
  planIncludesAI,
} from "utils/aiCredits";

// Fallback options (used if /options endpoint is unavailable)
const FALLBACK_OPTIONS = {
  platforms: [
    { value: "instagram", label: "Instagram" },
    { value: "facebook", label: "Facebook" },
    { value: "twitter", label: "Twitter / X" },
    { value: "linkedin", label: "LinkedIn" },
    { value: "tiktok", label: "TikTok" },
    { value: "telegram", label: "Telegram" },
    { value: "youtube", label: "YouTube" },
    { value: "threads", label: "Threads" },
  ],
  post_types: [
    { value: "promotional", label: "Promotional" },
    { value: "educational", label: "Educational" },
    { value: "engagement", label: "Engagement" },
    { value: "announcement", label: "Announcement" },
    { value: "storytelling", label: "Storytelling" },
    { value: "behind_the_scenes", label: "Behind the Scenes" },
    { value: "tips", label: "Tips & Tricks" },
    { value: "testimonial", label: "Testimonial" },
    { value: "seasonal", label: "Seasonal" },
    { value: "poll", label: "Poll" },
    { value: "carousel", label: "Carousel" },
    { value: "user_generated", label: "User Generated" },
  ],
  tones: [
    { value: "professional", label: "Professional" },
    { value: "casual", label: "Casual" },
    { value: "humorous", label: "Humorous" },
    { value: "inspirational", label: "Inspirational" },
    { value: "urgent", label: "Urgent" },
    { value: "friendly", label: "Friendly" },
    { value: "authoritative", label: "Authoritative" },
    { value: "edgy", label: "Edgy" },
    { value: "empathetic", label: "Empathetic" },
    { value: "luxurious", label: "Luxurious" },
  ],
  cta_types: [
    { value: "signup", label: "Sign Up" },
    { value: "learn_more", label: "Learn More" },
    { value: "shop_now", label: "Shop Now" },
    { value: "contact_us", label: "Contact Us" },
    { value: "download", label: "Download" },
    { value: "subscribe", label: "Subscribe" },
    { value: "book_now", label: "Book Now" },
    { value: "get_started", label: "Get Started" },
    { value: "try_free", label: "Try Free" },
    { value: "donate", label: "Donate" },
  ],
  languages: [
    { value: "en", label: "English" },
    { value: "ru", label: "Русский" },
    { value: "de", label: "Deutsch" },
    { value: "fr", label: "Français" },
    { value: "it", label: "Italiano" },
    { value: "es", label: "Español" },
    { value: "pt", label: "Português" },
    { value: "zh", label: "中文" },
    { value: "ja", label: "日本語" },
    { value: "ko", label: "한국어" },
    { value: "ar", label: "العربية" },
    { value: "hi", label: "हिन्दी" },
    { value: "tr", label: "Türkçe" },
    { value: "pl", label: "Polski" },
    { value: "nl", label: "Nederlands" },
  ],
  lengths: [
    { value: "short", label: "Short" },
    { value: "medium", label: "Medium" },
    { value: "long", label: "Long" },
  ],
};

const inputSx = {
  backgroundColor: "var(--hedear-bg)",
  color: "var(--input-text)",
  borderRadius: "7px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#42C0C7" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#42C0C7" },
};

const selectSx = {
  ...inputSx,
  "& .MuiSelect-icon": { color: "var(--text-color)" },
};

const labelSx = {
  color: "var(--text-color)",
  "&.Mui-focused": { color: "#42C0C7" },
};

const accordionSx = {
  backgroundColor: "transparent",
  boxShadow: "none",
  color: "var(--text-color)",
  "&:before": { display: "none" },
  "& .MuiAccordionSummary-root": {
    padding: 0,
    minHeight: "40px",
  },
  "& .MuiAccordionSummary-content": {
    margin: "8px 0",
  },
  "& .MuiAccordionDetails-root": {
    padding: "0 0 16px",
  },
};

/**
 * Normalizes an option item to { value, label } format.
 * Handles strings, objects with value/label, or objects with name/title.
 * @param {*} item - Item to normalize
 * @returns {Object|null} Normalized option or null
 */
function normalizeOption(item) {
  if (typeof item === "string") {
    return { value: item, label: item.charAt(0).toUpperCase() + item.slice(1) };
  }
  if (item && typeof item === "object") {
    const value = item.value || item.id || item.name || item.key;
    const label = item.label || item.title || item.name || value;
    if (value) return { value, label };
  }
  return null;
}

/**
 * Merges backend options with fallback, keeping fallback for missing/empty arrays.
 * @param {Object} fallback - Current fallback options
 * @param {Object} remote - Backend response data
 * @returns {Object} Merged options
 */
function mergeOptions(fallback, remote) {
  const keys = Object.keys(fallback);
  const merged = { ...fallback };

  keys.forEach((key) => {
    const val = remote?.[key];
    if (Array.isArray(val) && val.length > 0) {
      const normalized = val.map(normalizeOption).filter(Boolean);
      if (normalized.length > 0) {
        merged[key] = normalized;
      }
    }
  });

  return merged;
}

export default function AIGenerateDialog({
  open,
  onClose,
  onApply,
  tr,
  selectedAccounts,
  accounts,
}) {
  const { metaData, refreshCredits } = useContext(AppContext);
  const aiCredits = metaData?.ai_credits || null;

  // "No AI in your plan" must be derived from the *plan*, not the wallet's
  // current allowance. A paid plan whose allowance is 0 (grant not yet synced,
  // or simply out of credits) is a different state and must not be shown as a
  // plan limitation — that was the misleading "unavailable in your tariff" bug.
  const currentPlan = metaData?.subscription?.package;
  const planHasNoAI = !!currentPlan && !planIncludesAI(currentPlan);

  const [options, setOptions] = useState(FALLBACK_OPTIONS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(-1);
  const [activeResultTab, setActiveResultTab] = useState(0);
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);
  const [creditShortfall, setCreditShortfall] = useState(null);

  // post_generation is a flat 1-credit charge regardless of how many variations
  // are requested (the backend deducts per call, not per variation).
  const generationCost = creditCostFor("post_generation");

  // Paid plan, but nothing spendable right now → truthful "out of credits"
  // (buy a pack), as opposed to "your plan has no AI" (upgrade).
  const outOfCredits =
    !planHasNoAI && aiCredits ? (aiCredits.balance ?? 0) < generationCost : false;

  // Form state
  const [platform, setPlatform] = useState("");
  const [topic, setTopic] = useState("");
  const [postType, setPostType] = useState("");
  const [tone, setTone] = useState("");
  const [variationsCount, setVariationsCount] = useState(1);
  const [creativity, setCreativity] = useState(0.7);
  const [additionalInstructions, setAdditionalInstructions] = useState("");

  // Keywords
  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState("");

  // Content settings
  const [language, setLanguage] = useState("en");
  const [length, setLength] = useState("medium");
  const [includeHashtags, setIncludeHashtags] = useState(true);
  const [hashtagCount, setHashtagCount] = useState(5);
  const [includeEmojis, setIncludeEmojis] = useState(true);
  const [includeCta, setIncludeCta] = useState(false);
  const [ctaType, setCtaType] = useState("");
  const [customCta, setCustomCta] = useState("");

  // Business context
  const [brandName, setBrandName] = useState("");
  const [industry, setIndustry] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [brandVoice, setBrandVoice] = useState("");
  const [usp, setUsp] = useState("");

  // Product
  const [productName, setProductName] = useState("");
  const [productDescription, setProductDescription] = useState("");
  const [productPrice, setProductPrice] = useState("");
  const [productUrl, setProductUrl] = useState("");
  const [productPromotion, setProductPromotion] = useState("");

  // Auto-detect platform from selected accounts
  useEffect(() => {
    if (!platform && selectedAccounts?.length && accounts?.length) {
      const acc = accounts.find((a) => selectedAccounts.includes(a.id));
      if (acc?.social_type) {
        const mapped = acc.social_type.toLowerCase();
        const valid = (options.platforms || FALLBACK_OPTIONS.platforms).map(
          (p) => p.value,
        );
        if (valid.includes(mapped)) {
          setPlatform(mapped);
        }
      }
    }
  }, [open, selectedAccounts, accounts, platform, options.platforms]);

  // Load options from backend
  useEffect(() => {
    if (open) {
      _request.generatePost
        .getOptions()
        .then((resp) => {
          if (resp?.isOk && resp?.data) {
            setOptions((prev) => mergeOptions(prev, resp.data));
          }
        })
        .catch(() => {
          /* use fallback */
        });
    }
  }, [open]);

  const resetForm = useCallback(() => {
    setResults(null);
    setError(null);
    setActiveResultTab(0);
  }, []);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (kw && keywords.length < 10 && !keywords.includes(kw)) {
      setKeywords([...keywords, kw]);
      setKeywordInput("");
    }
  };

  const removeKeyword = (index) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  const handleGenerate = async () => {
    if (!platform || !topic.trim()) return;

    setLoading(true);
    setError(null);
    setResults(null);

    const params = { platform, topic: topic.trim() };

    if (postType) params.post_type = postType;
    if (tone) params.tone = tone;
    if (variationsCount > 1) params.variations_count = variationsCount;
    if (creativity !== 0.7) params.creativity = creativity;
    if (additionalInstructions.trim())
      params.additional_instructions = additionalInstructions.trim();
    if (keywords.length > 0) params.keywords = keywords;

    // Content settings
    const cs = {};
    if (language !== "en") cs.language = language;
    if (length !== "medium") cs.length = length;
    cs.include_hashtags = includeHashtags;
    if (includeHashtags && hashtagCount !== 5) cs.hashtag_count = hashtagCount;
    cs.include_emojis = includeEmojis;
    cs.include_cta = includeCta;
    if (includeCta && ctaType) cs.cta_type = ctaType;
    if (includeCta && ctaType === "custom" && customCta.trim())
      cs.custom_cta = customCta.trim();
    if (Object.keys(cs).length > 0) params.content_settings = cs;

    // Business context
    const bc = {};
    if (brandName.trim()) bc.brand_name = brandName.trim();
    if (industry.trim()) bc.industry = industry.trim();
    if (targetAudience.trim()) bc.target_audience = targetAudience.trim();
    if (brandVoice.trim()) bc.brand_voice = brandVoice.trim();
    if (usp.trim()) bc.unique_selling_point = usp.trim();
    if (Object.keys(bc).length > 0) params.business_context = bc;

    // Product
    const prod = {};
    if (productName.trim()) prod.name = productName.trim();
    if (productDescription.trim()) prod.description = productDescription.trim();
    if (productPrice.trim()) prod.price = productPrice.trim();
    if (productUrl.trim()) prod.url = productUrl.trim();
    if (productPromotion.trim()) prod.promotion = productPromotion.trim();
    if (Object.keys(prod).length > 0) params.product = prod;

    try {
      const resp = await _request.generatePost.generate(params);

      if (resp?.isOk) {
        setResults(resp.data);
        if (resp.data?.credits_remaining !== undefined && refreshCredits) {
          refreshCredits();
        }
      } else if (resp?.isInsufficientCredits) {
        setCreditShortfall(extractCreditShortfall(resp));
        setBuyCreditsOpen(true);
      } else {
        setError(resp?.error || tr("aiGenerate.errorGenerating"));
      }
    } catch (e) {
      setError(tr("aiGenerate.errorGenerating"));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(-1), 2000);
  };

  const handleApply = (variation) => {
    onApply({
      text: variation.text || variation.content || "",
      title: variation.title || "",
      hashtags: variation.hashtags || [],
    });
    handleClose();
  };

  const platformOptions = options.platforms || FALLBACK_OPTIONS.platforms;
  const postTypeOptions = options.post_types || FALLBACK_OPTIONS.post_types;
  const toneOptions = options.tones || FALLBACK_OPTIONS.tones;
  const ctaOptions = options.cta_types || FALLBACK_OPTIONS.cta_types;
  const languageOptions = options.languages || FALLBACK_OPTIONS.languages;
  const lengthOptions = options.lengths || FALLBACK_OPTIONS.lengths;

  const isValid = platform && topic.trim().length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "12px",
          backgroundColor: "var(--background-menu)",
          maxHeight: "90vh",
        },
      }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 24px",
          borderBottom: "1px solid #4f5154",
        }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutoAwesomeIcon sx={{ color: "#42C0C7" }} />
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: "bold",
              color: "var(--text-color)",
            }}>
            {tr("aiGenerate.title")}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: "var(--text-color)" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {/* Results view */}
        {results ? (
          <ResultsView
            results={results}
            activeTab={activeResultTab}
            onTabChange={setActiveResultTab}
            onCopy={handleCopy}
            copiedIndex={copiedIndex}
            onApply={handleApply}
            onBack={resetForm}
            tr={tr}
          />
        ) : (
          /* Form view */
          <>
            {/* Required fields */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={labelSx}>
                  {tr("aiGenerate.platform")} *
                </InputLabel>
                <Select
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  label={`${tr("aiGenerate.platform")} *`}
                  sx={selectSx}>
                  {platformOptions.map((p) => (
                    <MenuItem key={p.value} value={p.value}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TextField
              fullWidth
              size="small"
              label={`${tr("aiGenerate.topicLabel")} *`}
              placeholder={tr("aiGenerate.topicPlaceholder")}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              InputLabelProps={{ sx: labelSx }}
              InputProps={{ sx: inputSx }}
            />

            {/* Quick settings row */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={labelSx}>
                  {tr("aiGenerate.postType")}
                </InputLabel>
                <Select
                  value={postType}
                  onChange={(e) => setPostType(e.target.value)}
                  label={tr("aiGenerate.postType")}
                  sx={selectSx}>
                  <MenuItem value="">{tr("aiGenerate.auto")}</MenuItem>
                  {postTypeOptions.map((p) => (
                    <MenuItem key={p.value} value={p.value}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel sx={labelSx}>{tr("aiGenerate.tone")}</InputLabel>
                <Select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  label={tr("aiGenerate.tone")}
                  sx={selectSx}>
                  <MenuItem value="">{tr("aiGenerate.auto")}</MenuItem>
                  {toneOptions.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Variations & Creativity */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
              }}>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "var(--text-color)", mb: 0.5 }}>
                  {tr("aiGenerate.variations")}: {variationsCount}
                </Typography>
                <Slider
                  value={variationsCount}
                  onChange={(_, v) => setVariationsCount(v)}
                  min={1}
                  max={5}
                  step={1}
                  marks
                  sx={{ color: "#42C0C7" }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "var(--text-color)", mb: 0.5 }}>
                  {tr("aiGenerate.creativity")}: {creativity.toFixed(1)}
                </Typography>
                <Slider
                  value={creativity}
                  onChange={(_, v) => setCreativity(v)}
                  min={0}
                  max={1}
                  step={0.1}
                  sx={{ color: "#42C0C7" }}
                />
              </Box>
            </Box>

            {/* Keywords */}
            <Box>
              <Typography
                variant="body2"
                sx={{ color: "var(--text-color)", mb: 0.5 }}>
                {tr("aiGenerate.keywords")} ({keywords.length}/10)
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                {keywords.map((kw, i) => (
                  <Chip
                    key={i}
                    label={kw}
                    size="small"
                    onDelete={() => removeKeyword(i)}
                    sx={{ backgroundColor: "#42C0C7", color: "#fff" }}
                  />
                ))}
              </Box>
              {keywords.length < 10 && (
                <TextField
                  size="small"
                  fullWidth
                  placeholder={tr("aiGenerate.addKeyword")}
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addKeyword();
                    }
                  }}
                  InputProps={{ sx: inputSx }}
                />
              )}
            </Box>

            {/* Content Settings — collapsible */}
            <Accordion sx={accordionSx}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#42C0C7" }} />}>
                <Typography
                  sx={{ color: "#42C0C7", fontWeight: 500, fontSize: 14 }}>
                  {tr("aiGenerate.contentSettings")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: { xs: "column", sm: "row" },
                    }}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={labelSx}>
                        {tr("aiGenerate.language")}
                      </InputLabel>
                      <Select
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        label={tr("aiGenerate.language")}
                        sx={selectSx}>
                        {languageOptions.map((l) => (
                          <MenuItem key={l.value} value={l.value}>
                            {l.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={labelSx}>
                        {tr("aiGenerate.length")}
                      </InputLabel>
                      <Select
                        value={length}
                        onChange={(e) => setLength(e.target.value)}
                        label={tr("aiGenerate.length")}
                        sx={selectSx}>
                        {lengthOptions.map((l) => (
                          <MenuItem key={l.value} value={l.value}>
                            {l.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeHashtags}
                          onChange={(e) => setIncludeHashtags(e.target.checked)}
                          sx={{
                            "& .Mui-checked": { color: "#42C0C7" },
                            "& .Mui-checked+.MuiSwitch-track": {
                              backgroundColor: "#42C0C7",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          sx={{ color: "var(--text-color)" }}>
                          {tr("aiGenerate.includeHashtags")}
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeEmojis}
                          onChange={(e) => setIncludeEmojis(e.target.checked)}
                          sx={{
                            "& .Mui-checked": { color: "#42C0C7" },
                            "& .Mui-checked+.MuiSwitch-track": {
                              backgroundColor: "#42C0C7",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          sx={{ color: "var(--text-color)" }}>
                          {tr("aiGenerate.includeEmojis")}
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeCta}
                          onChange={(e) => setIncludeCta(e.target.checked)}
                          sx={{
                            "& .Mui-checked": { color: "#42C0C7" },
                            "& .Mui-checked+.MuiSwitch-track": {
                              backgroundColor: "#42C0C7",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          sx={{ color: "var(--text-color)" }}>
                          {tr("aiGenerate.includeCta")}
                        </Typography>
                      }
                    />
                  </Box>

                  {includeHashtags && (
                    <Box sx={{ maxWidth: 200 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "var(--text-color)", mb: 0.5 }}>
                        {tr("aiGenerate.hashtagCount")}: {hashtagCount}
                      </Typography>
                      <Slider
                        value={hashtagCount}
                        onChange={(_, v) => setHashtagCount(v)}
                        min={1}
                        max={30}
                        sx={{ color: "#42C0C7" }}
                      />
                    </Box>
                  )}

                  {includeCta && (
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel sx={labelSx}>
                          {tr("aiGenerate.ctaType")}
                        </InputLabel>
                        <Select
                          value={ctaType}
                          onChange={(e) => setCtaType(e.target.value)}
                          label={tr("aiGenerate.ctaType")}
                          sx={selectSx}>
                          <MenuItem value="">{tr("aiGenerate.auto")}</MenuItem>
                          {ctaOptions.map((c) => (
                            <MenuItem key={c.value} value={c.value}>
                              {c.label}
                            </MenuItem>
                          ))}
                          <MenuItem value="custom">
                            {tr("aiGenerate.customCta")}
                          </MenuItem>
                        </Select>
                      </FormControl>
                      {ctaType === "custom" && (
                        <TextField
                          fullWidth
                          size="small"
                          label={tr("aiGenerate.customCtaText")}
                          value={customCta}
                          onChange={(e) => setCustomCta(e.target.value)}
                          InputLabelProps={{ sx: labelSx }}
                          InputProps={{ sx: inputSx }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Business Context — collapsible */}
            <Accordion sx={accordionSx}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#42C0C7" }} />}>
                <Typography
                  sx={{ color: "#42C0C7", fontWeight: 500, fontSize: 14 }}>
                  {tr("aiGenerate.businessContext")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: { xs: "column", sm: "row" },
                    }}>
                    <TextField
                      fullWidth
                      size="small"
                      label={tr("aiGenerate.brandName")}
                      value={brandName}
                      onChange={(e) => setBrandName(e.target.value)}
                      InputLabelProps={{ sx: labelSx }}
                      InputProps={{ sx: inputSx }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label={tr("aiGenerate.industry")}
                      value={industry}
                      onChange={(e) => setIndustry(e.target.value)}
                      InputLabelProps={{ sx: labelSx }}
                      InputProps={{ sx: inputSx }}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    label={tr("aiGenerate.targetAudience")}
                    value={targetAudience}
                    onChange={(e) => setTargetAudience(e.target.value)}
                    InputLabelProps={{ sx: labelSx }}
                    InputProps={{ sx: inputSx }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label={tr("aiGenerate.brandVoice")}
                    value={brandVoice}
                    onChange={(e) => setBrandVoice(e.target.value)}
                    InputLabelProps={{ sx: labelSx }}
                    InputProps={{ sx: inputSx }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label={tr("aiGenerate.usp")}
                    value={usp}
                    onChange={(e) => setUsp(e.target.value)}
                    InputLabelProps={{ sx: labelSx }}
                    InputProps={{ sx: inputSx }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Product — collapsible */}
            <Accordion sx={accordionSx}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#42C0C7" }} />}>
                <Typography
                  sx={{ color: "#42C0C7", fontWeight: 500, fontSize: 14 }}>
                  {tr("aiGenerate.product")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: { xs: "column", sm: "row" },
                    }}>
                    <TextField
                      fullWidth
                      size="small"
                      label={tr("aiGenerate.productName")}
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      InputLabelProps={{ sx: labelSx }}
                      InputProps={{ sx: inputSx }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label={tr("aiGenerate.productPrice")}
                      value={productPrice}
                      onChange={(e) => setProductPrice(e.target.value)}
                      InputLabelProps={{ sx: labelSx }}
                      InputProps={{ sx: inputSx }}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    label={tr("aiGenerate.productDescription")}
                    value={productDescription}
                    onChange={(e) => setProductDescription(e.target.value)}
                    multiline
                    rows={2}
                    InputLabelProps={{ sx: labelSx }}
                    InputProps={{ sx: inputSx }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label={tr("aiGenerate.productUrl")}
                    value={productUrl}
                    onChange={(e) => setProductUrl(e.target.value)}
                    InputLabelProps={{ sx: labelSx }}
                    InputProps={{ sx: inputSx }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label={tr("aiGenerate.productPromotion")}
                    value={productPromotion}
                    onChange={(e) => setProductPromotion(e.target.value)}
                    InputLabelProps={{ sx: labelSx }}
                    InputProps={{ sx: inputSx }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Additional instructions */}
            <TextField
              fullWidth
              size="small"
              label={tr("aiGenerate.additionalInstructions")}
              placeholder={tr("aiGenerate.additionalInstructionsPlaceholder")}
              value={additionalInstructions}
              onChange={(e) => setAdditionalInstructions(e.target.value)}
              multiline
              rows={2}
              InputLabelProps={{ sx: labelSx }}
              InputProps={{ sx: inputSx }}
            />
          </>
        )}
      </DialogContent>

      {/* Footer */}
      {!results && (
        <DialogActions
          sx={{
            padding: "12px 24px",
            borderTop: "1px solid #4f5154",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
          {/* Credit cost / plan info */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 0.5,
            }}>
            {planHasNoAI ? (
              <Typography variant="caption" sx={{ color: "#FE8484" }}>
                {tr("aiCredits.noCreditsInPlan")}{" "}
                <Link
                  to="/app/pricing"
                  onClick={handleClose}
                  style={{ color: "#42C0C7" }}>
                  {tr("aiCredits.upgradePlan")}
                </Link>
              </Typography>
            ) : aiCredits ? (
              <>
                {outOfCredits && (
                  <Typography variant="caption" sx={{ color: "#FE8484" }}>
                    {tr("aiCredits.outOfCredits")}{" "}
                    <Typography
                      component="span"
                      variant="caption"
                      onClick={() => setBuyCreditsOpen(true)}
                      sx={{ color: "#42C0C7", cursor: "pointer" }}>
                      {tr("aiCredits.buyCredits")}
                    </Typography>
                  </Typography>
                )}
                <Chip
                  icon={<AutoAwesomeIcon sx={{ fontSize: "13px !important" }} />}
                  label={`${tr("aiCredits.cost")}: ${generationCost} · ${tr("aiCredits.remaining")}: ${aiCredits.monthly_remaining}`}
                  size="small"
                  sx={{
                    backgroundColor:
                      aiCredits.monthly_remaining === 0
                        ? "#FE848420"
                        : "#42C0C720",
                    color:
                      aiCredits.monthly_remaining === 0 ? "#FE8484" : "#42C0C7",
                    border: `1px solid ${aiCredits.monthly_remaining === 0 ? "#FE848460" : "#42C0C760"}`,
                    fontSize: 11,
                    "& .MuiChip-icon": {
                      color:
                        aiCredits.monthly_remaining === 0
                          ? "#FE8484"
                          : "#42C0C7",
                    },
                  }}
                />
                {variationsCount > 1 && (
                  <Typography
                    variant="caption"
                    sx={{ color: "var(--text-color)", opacity: 0.6, fontSize: 10 }}>
                    {tr("aiCredits.perGenerationNote")}
                  </Typography>
                )}
              </>
            ) : null}
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={handleClose} sx={{ color: "var(--text-color)" }}>
              {tr("aiGenerate.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={!isValid || loading || planHasNoAI}
              startIcon={
                loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <AutoAwesomeIcon />
                )
              }
              sx={{
                backgroundColor: "#42C0C7",
                "&:hover": { backgroundColor: "#3aa8ae" },
                "&.Mui-disabled": { backgroundColor: "#42C0C750" },
              }}>
              {loading ? tr("aiGenerate.generating") : tr("aiGenerate.generate")}
            </Button>
          </Box>
        </DialogActions>
      )}

      <BuyCreditsModal
        open={buyCreditsOpen}
        onClose={() => {
          setBuyCreditsOpen(false);
          setCreditShortfall(null);
        }}
        isTrial={planHasNoAI}
        shortfall={creditShortfall}
      />
    </Dialog>
  );
}

// Results sub-component
function ResultsView({
  results,
  activeTab,
  onTabChange,
  onCopy,
  copiedIndex,
  onApply,
  onBack,
  tr,
}) {
  const variations = results?.variations || results?.posts || [results];
  const tips = results?.timing_tips || results?.best_time;
  const mediaRecs = results?.media_recommendations || results?.media;

  return (
    <Box>
      <Button
        onClick={onBack}
        sx={{ color: "#42C0C7", mb: 1, textTransform: "none" }}>
        ← {tr("aiGenerate.backToForm")}
      </Button>

      {variations.length > 1 && (
        <Tabs
          value={activeTab}
          onChange={(_, v) => onTabChange(v)}
          sx={{
            mb: 2,
            "& .MuiTab-root": {
              color: "var(--text-color)",
              textTransform: "none",
            },
            "& .Mui-selected": { color: "#42C0C7 !important" },
            "& .MuiTabs-indicator": { backgroundColor: "#42C0C7" },
          }}>
          {variations.map((_, i) => (
            <Tab key={i} label={`${tr("aiGenerate.variation")} ${i + 1}`} />
          ))}
        </Tabs>
      )}

      {variations.map((variation, i) => (
        <Box key={i} sx={{ display: activeTab === i ? "block" : "none" }}>
          {/* Post content */}
          <Box
            sx={{
              backgroundColor: "var(--hedear-bg)",
              borderRadius: "8px",
              padding: "16px",
              mb: 2,
              position: "relative",
            }}>
            <Typography
              sx={{
                color: "var(--text-color)",
                whiteSpace: "pre-wrap",
                fontSize: 14,
                lineHeight: 1.6,
              }}>
              {variation.text || variation.content || ""}
            </Typography>

            {/* Hashtags */}
            {variation.hashtags?.length > 0 && (
              <Box
                sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {variation.hashtags.map((tag, j) => (
                  <Chip
                    key={j}
                    label={tag.startsWith("#") ? tag : `#${tag}`}
                    size="small"
                    sx={{
                      backgroundColor: "#42C0C720",
                      color: "#42C0C7",
                      fontSize: 12,
                    }}
                  />
                ))}
              </Box>
            )}

            {/* Action buttons */}
            <Box
              sx={{
                mt: 2,
                display: "flex",
                gap: 1,
                justifyContent: "flex-end",
              }}>
              <Button
                size="small"
                startIcon={
                  copiedIndex === i ? <CheckIcon /> : <ContentCopyIcon />
                }
                onClick={() => handleCopyFull(variation, onCopy, i)}
                sx={{
                  color: copiedIndex === i ? "#4caf50" : "var(--text-color)",
                  textTransform: "none",
                }}>
                {copiedIndex === i
                  ? tr("aiGenerate.copied")
                  : tr("aiGenerate.copy")}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => onApply(variation)}
                sx={{
                  backgroundColor: "#42C0C7",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#3aa8ae" },
                }}>
                {tr("aiGenerate.useThis")}
              </Button>
            </Box>
          </Box>

          {/* Tips */}
          {tips && (
            <Box
              sx={{
                backgroundColor: "var(--hedear-bg)",
                borderRadius: "8px",
                padding: "12px 16px",
                mb: 2,
              }}>
              <Typography
                variant="body2"
                sx={{ color: "#42C0C7", fontWeight: 600, mb: 0.5 }}>
                {tr("aiGenerate.timingTips")}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "var(--text-color)", fontSize: 13 }}>
                {typeof tips === "string" ? tips : JSON.stringify(tips)}
              </Typography>
            </Box>
          )}

          {/* Media recommendations */}
          {mediaRecs && (
            <Box
              sx={{
                backgroundColor: "var(--hedear-bg)",
                borderRadius: "8px",
                padding: "12px 16px",
              }}>
              <Typography
                variant="body2"
                sx={{ color: "#42C0C7", fontWeight: 600, mb: 0.5 }}>
                {tr("aiGenerate.mediaRecommendations")}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "var(--text-color)", fontSize: 13 }}>
                {typeof mediaRecs === "string"
                  ? mediaRecs
                  : JSON.stringify(mediaRecs)}
              </Typography>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

function handleCopyFull(variation, onCopy, index) {
  const text = variation.text || variation.content || "";
  const hashtags = (variation.hashtags || [])
    .map((t) => (t.startsWith("#") ? t : `#${t}`))
    .join(" ");
  const full = hashtags ? `${text}\n\n${hashtags}` : text;
  onCopy(full, index);
}
